import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../atoms';
import { updateLanguage } from '../../../features/global/globalSlice';
import { setHasUnsavedProfileChanges } from '../../../features/global/profileEditSlice';
import { setLanguageChangedBeforeLogin } from '../../../features/auth/authSlice';
import { useAppDispatch, useAppSelector } from '../../../common/hooks';
import { NavLink } from '../../molecules/Nav/Link';
import { NavDropdown } from '../../molecules/Nav/Dropdown';
import { NAV_LABEL_CHEVRON_GAP } from '../../molecules/Nav/constants';
import { ConfirmationCentered } from '../../molecules/ConfirmationDialog/ConfirmationDialog';
import { uipApiInstance } from '../../../services';

// Trigger spacing, in theme.spacing units.
const ICON_LABEL_GAP = 0.5;
const TRIGGER_PADDING_Y = 0.625;
// In the header the nav list spaces the actions; the login page has no such list, so the trigger keeps its own room.
const LOGIN_PADDING_RIGHT = 2.5;

export const Container = styled('span')<{ isActive: boolean }>(({ isActive = false }) => ({
    display: 'flex',
    alignItems: 'center',
    '& > .chevron-icon': {
        width: '15px',
        height: '10px',
        ...(isActive && {
            transform: 'rotate(180deg)'
        })
    }
}));

export const TenantName = styled('span')(({ theme }) => ({
    // Adds to the container's gap to space the label from the chevron.
    marginRight: theme.spacing(NAV_LABEL_CHEVRON_GAP - ICON_LABEL_GAP),
    textTransform: 'uppercase'
}));

const DropdownButton = styled('button')<{ isCurrentLanguage: boolean }>(({ isCurrentLanguage = false }) => ({
    fontFamily: 'TT Commons Pro Condensed',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '20px',
    letterSpacing: '0.02em',
    color: isCurrentLanguage ? '#FF0000' : '#3769FF',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    marginRight: 'auto',
    textTransform: 'uppercase',
    padding: 0,
    '&:not(:last-child)': {
        marginBottom: '8px'
    },
    '&:hover': {
        color: '#FF0000'
    }
}));

const DropdownLegend = styled('h2')(() => ({
    fontFamily: 'TT Commons Pro',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
    color: '#354560',
    margin: 0,
    marginBottom: '18px'
}));

const LanguageList = styled('ul')(() => ({
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
}));

const LanguageListItem = styled('li')(() => ({
    display: 'flex'
}));

type Props = {
    isDropdownActive: boolean;
    onToggleDropdown(): void;
    isLogInContainer?: boolean;
};

const laguageOptions = [
    { value: 'en', label: 'EN (US)', dpLabel: 'English (US)', lang: 'en-US' },
    { value: 'es', label: 'ES (MX)', dpLabel: 'Español (México)', lang: 'es-MX' }
];

const LanguageSelectorComponent = ({ isDropdownActive, onToggleDropdown, isLogInContainer }: Props) => {
    const { i18n, t } = useTranslation();
    const [languageObject, setLanguageObject] = useState<{ value: string; label: string; dpLabel: string } | null>(
        null
    );
    // Language the user selected but hasn't been applied yet because of unsaved Client/Co-Client changes.
    const [pendingLanguageObj, setPendingLanguageObj] = useState<{
        value: string;
        label: string;
        dpLabel: string;
    } | null>(null);

    useEffect(() => {
        // const storedLanguage = localStorage.getItem('userLanguage');
        if (i18n.language) {
            const languageObj = laguageOptions.find((option) => option.value === i18n.language);
            if (languageObj) {
                setLanguageObject(languageObj);
            }
        }
    }, [i18n.language]);

    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const userEmail = useAppSelector((state) => state?.auth?.user?.userEmail);
    const isAuthenticated = useAppSelector((state) => state?.auth?.isAuthenticated);
    const languageChangedBeforeLogin = useAppSelector((state) => state.auth.languageChangedBeforeLogin);
    const hasUnsavedProfileChanges = useAppSelector((state) => state.profileEdit.hasUnsavedProfileChanges);

    const dispatch = useAppDispatch();

    /** When user toggles language through UI dropdown, update the language on Okta profile */
    const updateLanguageOnOkta = async (languageValue: string) => {
        const locale = languageValue === 'en' ? 'en_US' : 'es_MX';
        const data = {
            username: userEmail,
            from_tenant: tenant,
            locale
        };
        try {
            await uipApiInstance({
                method: 'POST',
                url: '/api/advice/updateuserprofile',
                withCredentials: false,
                data
            });
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        const updateLanguage = async () => {
            if (languageChangedBeforeLogin) {
                const language = localStorage.getItem('userLanguage') || 'en';
                await updateLanguageOnOkta(language);
            }
        };
        updateLanguage();
    }, []);

    const handleLanguageChange = async (event: {
        target: { value: { value: string; label: string; dpLabel: string } };
    }) => {
        const newLanguageObj = event.target.value;
        const isNewLanguageEqualsOld = localStorage.getItem('userLanguage') === newLanguageObj.value;

        // If the user is switching to a different language while there are unsaved
        // Client/Co-Client changes, ask them to confirm before discarding those edits.
        if (!isNewLanguageEqualsOld && hasUnsavedProfileChanges) {
            setPendingLanguageObj(newLanguageObj);
            onToggleDropdown();
            return;
        }

        await applyLanguageChange(newLanguageObj, isNewLanguageEqualsOld);
        onToggleDropdown(); // Close dropdown after selection
    };

    const applyLanguageChange = async (
        newLanguageObj: { value: string; label: string; dpLabel: string },
        isNewLanguageEqualsOld: boolean
    ) => {
        setLanguageObject(newLanguageObj);
        localStorage.setItem('userLanguage', newLanguageObj.value);
        if (!isNewLanguageEqualsOld) {
            dispatch(updateLanguage({ tenantId: tenant, language: newLanguageObj.value, i18n })); // Update Redux state and i18n language
            if (userEmail && tenant) {
                await updateLanguageOnOkta(newLanguageObj.value);
            }
        }

        // If user is not authenticated, mark that language was changed before login
        if (!isAuthenticated) {
            dispatch(setLanguageChangedBeforeLogin(true));
        }
    };

    // User chose to discard the unsaved changes and proceed with the language switch.
    const handleDiscardAndSwitchLanguage = async () => {
        const newLanguageObj = pendingLanguageObj;
        setPendingLanguageObj(null);
        dispatch(setHasUnsavedProfileChanges(false));
        if (newLanguageObj) {
            await applyLanguageChange(newLanguageObj, false);
        }
    };

    // User chose to stay on the current language and keep editing.
    const handleStayOnCurrentLanguage = () => {
        setPendingLanguageObj(null);
    };

    return (
        <>
            <NavLink onClick={onToggleDropdown} id="nav-tenant-button" aria-expanded={isDropdownActive}>
                <Container
                    isActive={isDropdownActive}
                    sx={(theme) => ({
                        color: isLogInContainer ? 'black' : 'white',
                        float: 'right',
                        display: 'flex',
                        alignItems: 'center',
                        paddingBlock: theme.spacing(TRIGGER_PADDING_Y),
                        ...(isLogInContainer && { paddingRight: theme.spacing(LOGIN_PADDING_RIGHT) }),
                        gap: theme.spacing(ICON_LABEL_GAP)
                    })}
                >
                    <LanguageIcon aria-hidden focusable={false} />
                    <TenantName>{languageObject?.label}</TenantName>
                    <Icon id="chevron-down" className="chevron-icon" />
                </Container>
            </NavLink>
            <NavDropdown
                isVisible={isDropdownActive}
                parentId={isLogInContainer ? 'nav-tenant-button-login' : 'nav-tenant-button'}
                onClose={onToggleDropdown}
            >
                <DropdownLegend>{t('TEXT_SELECT_LANGUAGE')}</DropdownLegend>
                <LanguageList>
                    {laguageOptions.map((languageObj) => (
                        <LanguageListItem key={languageObj.value}>
                            <DropdownButton
                                type="button"
                                lang={languageObj.lang}
                                isCurrentLanguage={languageObj.value === languageObject?.value}
                                onClick={() => handleLanguageChange({ target: { value: languageObj } })}
                            >
                                {languageObj.dpLabel}
                            </DropdownButton>
                        </LanguageListItem>
                    ))}
                </LanguageList>
            </NavDropdown>
            {pendingLanguageObj && (
                <ConfirmationCentered
                    open
                    title={t('DRAWER_CLOSE_MODAL_TITLE')}
                    content={t('DRAWER_CLOSE_MODAL_HEADER_TEXT')}
                    confirmButtonLabel={t('DRAWER_CLOSE_MODAL_HEADER_TEXT_STAY')}
                    cancelButtonLabel={t('DRAWER_CLOSE_MODAL_HEADER_TEXT_LEAVE')}
                    onConfirmation={handleStayOnCurrentLanguage}
                    onCancellation={handleDiscardAndSwitchLanguage}
                    onCloseIconClick={handleStayOnCurrentLanguage}
                />
            )}
        </>
    );
};

export default LanguageSelectorComponent;
